import FullScreenActivityIndicator from "@rentiohq/shared-frontend/dist/components/components/FullScreenActivityIndicator";
import { FullScreenErrorState } from "@rentiohq/shared-frontend/dist/components/components/FullScreenState";
import {
  FullScreenErrorState as FullScreenErrorState2,
  FullScreenSuccessState,
} from "@rentiohq/shared-frontend/dist/components/components/FullScreenState2";
import { PaymentRequestFetch } from "@rentiohq/shared-frontend/dist/components/components/PaymentRequestFetch";
import rentioTheme from "@rentiohq/shared-frontend/dist/components/theme/theme.rentio";
import * as paymentRequestActions from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.actions";
import * as paymentRequestSelectors from "@rentiohq/shared-frontend/dist/reduxV2/paymentRequest/paymentRequest.selectors";
import {
  EPaymentRequestStatus,
  IPaymentRequest,
} from "@rentiohq/shared-frontend/dist/types/paymentRequest.types";
import { EBanqupRequestStatus } from "@rentiohq/shared-frontend/dist/types/rentDeposit.types";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import * as paymentRequestUtils from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import { default as React } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router";
import { IRootState } from "redux/reducers";
import Page from "../../../../components/Page";
import withAuthenticationCheck from "../../../../hoc/withAuthenticationCheck";

let fetchDataInterval: NodeJS.Timeout | undefined;

const PaymentStatusBase = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const params = useParams();

  const paymentRequestId = params.paymentRequestId
    ? parseInt(params.paymentRequestId, 0)
    : undefined;

  const paymentRequest = useSelector((state: IRootState) =>
    paymentRequestId
      ? paymentRequestSelectors.getDetail(state, paymentRequestId)
      : undefined,
  );
  const isFetchingPaymentRequest = useSelector((state: IRootState) =>
    paymentRequestId
      ? paymentRequestSelectors.isFetchingDetail(state, paymentRequestId)
      : false,
  );

  // Data
  const fetchData = React.useCallback(() => {
    if (!paymentRequestId) {
      return;
    }

    if (isFetchingPaymentRequest) {
      return;
    }

    if (
      paymentRequest?.banqupRequestStatus &&
      [
        EBanqupRequestStatus.Accepted,
        EBanqupRequestStatus.Rejected,
        EBanqupRequestStatus.Terminated,
        EBanqupRequestStatus.AutoApproved,
      ].includes(paymentRequest?.banqupRequestStatus)
    ) {
      return;
    }

    // Only fetch new data for new or approved payment request
    if (
      paymentRequest &&
      paymentRequest.status !== EPaymentRequestStatus.New &&
      paymentRequest.status !== EPaymentRequestStatus.Approved
    ) {
      return;
    }

    dispatch(
      paymentRequestActions.getDetailStart.getAction({
        id: paymentRequestId,
      }),
    );
  }, [paymentRequest]);

  // Lifecycle
  React.useEffect(() => {
    fetchDataInterval = setInterval(fetchData, 2500);

    return () => {
      if (fetchDataInterval) {
        clearInterval(fetchDataInterval);
      }
    };
  }, [paymentRequest]);

  // Event handlers
  const handlePressClose = () => {
    navigate("/");
  };

  const handlePressHelp = () => {
    const url = getLocalizedText("payment.cta.help.url");
    window.open(url, "_blank");
  };

  const renderBanqupPaymentRequest = (paymentRequest: IPaymentRequest) => {
    switch (paymentRequest.banqupRequestStatus) {
      case EBanqupRequestStatus.InReview:
        return (
          <Page withScroll={false}>
            <FullScreenActivityIndicator />
          </Page>
        );

      // All 'success' states
      case EBanqupRequestStatus.Submitted:
      case EBanqupRequestStatus.Validated:
      case EBanqupRequestStatus.Accepted:
      case EBanqupRequestStatus.AutoApproved: {
        return (
          <Page withScroll={false} backgroundColor={rentioTheme.colors.success}>
            <FullScreenSuccessState
              title={getLocalizedText("payment.feedback.success.title")}
              info={getLocalizedText("payment.feedback.success.info")}
              primaryActionTitle={getLocalizedText("system.ok")}
              onPressPrimaryAction={handlePressClose}
            />
          </Page>
        );
      }

      case EBanqupRequestStatus.Rejected:
      case EBanqupRequestStatus.Terminated:
        return (
          <Page withScroll={false} backgroundColor={rentioTheme.colors.error}>
            <FullScreenErrorState2
              title={getLocalizedText("payment.feedback.error.title")}
              info={getLocalizedText("payment.feedback.error.info")}
              primaryActionTitle={getLocalizedText("system.ok")}
              onPressPrimaryAction={handlePressClose}
              extraActionTitle={getLocalizedText("payment.cta.help")}
              onPressExtraAction={handlePressHelp}
            />
          </Page>
        );

      default:
        return <Page withScroll={false} />;
    }
  };

  // Render
  const renderMangoPayPaymentRequest = (paymentRequest: IPaymentRequest) => {
    switch (paymentRequest.status) {
      case EPaymentRequestStatus.New:
      case EPaymentRequestStatus.Approved:
        return (
          <Page withScroll={false}>
            <FullScreenActivityIndicator />
          </Page>
        );

      // All 'success' states
      case EPaymentRequestStatus.Pending:
      case EPaymentRequestStatus.PaidIn:
      case EPaymentRequestStatus.Partial:
      case EPaymentRequestStatus.Transferring:
      case EPaymentRequestStatus.Transferred:
      case EPaymentRequestStatus.PayingOut:
      case EPaymentRequestStatus.Paid: {
        const postfix = paymentRequest.paymentMethod;

        const sepaBankDaysOffset =
          paymentRequestUtils.sepaDeductionDaysFromNow(paymentRequest);

        const titleKeyBase = "payment.feedback.success.title";
        let titleKey = postfix ? `${titleKeyBase}.${postfix}` : titleKeyBase;
        // Untranslated for payment method => use base key
        if (titleKey === getLocalizedText(titleKey)) {
          titleKey = titleKeyBase;
        }
        const title = getLocalizedText(titleKey, {
          sepaBankDaysOffset: `${sepaBankDaysOffset}`,
        });

        const infoKeyBase = "payment.feedback.success.info";
        let infoKey = postfix ? `${infoKeyBase}.${postfix}` : infoKeyBase;
        // Untranslated for payment method => use base key
        if (infoKey === getLocalizedText(infoKey)) {
          infoKey = infoKeyBase;
        }
        const info = getLocalizedText(infoKey, {
          sepaBankDaysOffset: `${sepaBankDaysOffset}`,
        });

        return (
          <Page withScroll={false} backgroundColor={rentioTheme.colors.success}>
            <FullScreenSuccessState
              title={title}
              info={info}
              primaryActionTitle={getLocalizedText("system.ok")}
              onPressPrimaryAction={handlePressClose}
            />
          </Page>
        );
      }

      case EPaymentRequestStatus.FailedTransfer:
      case EPaymentRequestStatus.FailedPaidIn:
      case EPaymentRequestStatus.FailedPayout:
      case EPaymentRequestStatus.FailedKyc:
      case EPaymentRequestStatus.Failed:
        return (
          <Page withScroll={false} backgroundColor={rentioTheme.colors.error}>
            <FullScreenErrorState2
              title={getLocalizedText("payment.feedback.error.title")}
              info={getLocalizedText("payment.feedback.error.info")}
              primaryActionTitle={getLocalizedText("system.ok")}
              onPressPrimaryAction={handlePressClose}
              extraActionTitle={getLocalizedText("payment.cta.help")}
              onPressExtraAction={handlePressHelp}
            />
          </Page>
        );

      default:
        return <Page withScroll={false} />;
    }
  };

  const renderPaymentRequest = (paymentRequest: IPaymentRequest) => {
    if (paymentRequest?.banqupRequestStatus) {
      return renderBanqupPaymentRequest(paymentRequest);
    }

    return renderMangoPayPaymentRequest(paymentRequest);
  };

  const renderLoading = () => (
    <Page withScroll={false}>
      <FullScreenActivityIndicator />
    </Page>
  );

  const renderError = (retry: () => void, error?: Error) => (
    <Page withScroll={false}>
      <FullScreenErrorState
        showIcon={false}
        error={error}
        title={getLocalizedText("general.error")}
        actionTitle={getLocalizedText("try.again")}
        onPressAction={retry}
      />
    </Page>
  );

  if (!paymentRequestId) {
    return null;
  }

  return (
    <PaymentRequestFetch
      id={paymentRequestId}
      renderPaymentRequest={renderPaymentRequest}
      renderLoading={renderLoading}
      renderError={renderError}
      paymentRequestV2Enabled={true}
    />
  );
};

export const PaymentStatus = withAuthenticationCheck(PaymentStatusBase);
